import { ERROR_MESSAGES } from 'app/pages/system/customFieldDefinitions/CustomFieldDefinitionsTable/CustomFieldDefinitionsTable.errors';
import { SUCCESS_MESSAGES } from 'app/pages/system/customFieldDefinitions/CustomFieldDefinitionsTable/CustomFieldDefinitionsTable.messages';
import { customFieldDefinitionsApi } from 'app/services/api/customFieldDefinitionsApi';

import { catchAsyncActionError } from 'utils/action';

import { showMessageAction } from 'store/global/actions';
import { selectCustomFields } from 'store/system/selectors/customFieldDefinitions.selectors';
import { CUSTOM_FIELDS_ACTION_TYPE } from 'store/system/types/customFieldDefinitions.types';
import { CUSTOM_FIELDS_EXPRESSION_SCHEMA_ACTION_TYPE } from 'store/system/types/customFieldsExpressionSchema.types';

// Mirror of BE CustomFieldDefinitionsExpressionDataProvider.MapValueType — keep in sync
// when new CustomFieldValueType entries are added BE-side.
const VALUE_TYPE_TO_SCHEMA_TYPE = {
  number: 'decimal',
  currency: 'decimal',
  percentage: 'decimal',
  boolean: 'bool',
  date: 'date',
  text: 'string',
};

const schemaTypeOf = valueType =>
  VALUE_TYPE_TO_SCHEMA_TYPE[String(valueType ?? '').toLowerCase()] ?? 'string';

const patchExpressionSchemaAction = ({
  target,
  removeKey = null,
  addKey = null,
  valueType = null,
}) => ({
  type: CUSTOM_FIELDS_EXPRESSION_SCHEMA_ACTION_TYPE.PATCH_SCHEMA,
  payload: { target, removeKey, addKey, valueType },
});

const setCustomFieldsAction = customFields => ({
  type: CUSTOM_FIELDS_ACTION_TYPE.SET_CUSTOM_FIELDS,
  payload: customFields,
});

const mergeCustomFieldsAction = customFields => ({
  type: CUSTOM_FIELDS_ACTION_TYPE.MERGE_CUSTOM_FIELDS,
  payload: customFields,
});

const setLoadingAction = isLoading => ({
  type: CUSTOM_FIELDS_ACTION_TYPE.SET_LOADING,
  payload: isLoading,
});

const clearErrorAction = () => ({
  type: CUSTOM_FIELDS_ACTION_TYPE.CLEAR_ERROR,
});

export const loadCustomFieldsAction = catchAsyncActionError(
  ({ merge = false, ...params } = {}) =>
    async dispatch => {
      dispatch(setLoadingAction(true));
      dispatch(clearErrorAction());

      const response = await customFieldDefinitionsApi.getCustomFieldDefinitions(params);
      const customFields = Array.isArray(response.data) ? response.data : [];
      dispatch(merge ? mergeCustomFieldsAction(customFields) : setCustomFieldsAction(customFields));
      dispatch(setLoadingAction(false));

      return customFields;
    },
  ERROR_MESSAGES.LOAD,
);

export const createCustomFieldAction = catchAsyncActionError(
  data => async dispatch => {
    dispatch(setLoadingAction(true));
    dispatch(clearErrorAction());

    try {
      const response = await customFieldDefinitionsApi.createCustomFieldDefinition(data);
      const createdField = response.data;

      if (createdField?.id) {
        dispatch({
          type: CUSTOM_FIELDS_ACTION_TYPE.ADD_CUSTOM_FIELD,
          payload: createdField,
        });

        if (createdField.target && createdField.expressionKey) {
          dispatch(
            patchExpressionSchemaAction({
              target: createdField.target,
              addKey: createdField.expressionKey,
              valueType: schemaTypeOf(createdField.valueType),
            }),
          );
        }
      }

      dispatch(showMessageAction({ message: SUCCESS_MESSAGES.CREATE, variant: 'success' }));

      return createdField;
    } finally {
      dispatch(setLoadingAction(false));
    }
  },
  ERROR_MESSAGES.CREATE,
);

export const updateCustomFieldAction = catchAsyncActionError(
  payload => async (dispatch, getState) => {
    dispatch(setLoadingAction(true));
    dispatch(clearErrorAction());

    try {
      const { id, ...data } = payload;

      if (!id) {
        throw new Error('Missing ID for update');
      }

      // Capture the pre-update CF so we can detect an expressionKey rename and
      // remove the old entry from the cached schema after the new one is added.
      const previous = selectCustomFields(getState()).find(field => field.id === id) ?? null;

      const response = await customFieldDefinitionsApi.updateCustomFieldDefinition(id, data);
      const completePayload = {
        ...payload,
        ...response.data,
        id,
        isEnabled: payload.isEnabled,
      };

      dispatch({
        type: CUSTOM_FIELDS_ACTION_TYPE.UPDATE_CUSTOM_FIELD,
        payload: completePayload,
      });

      const target = completePayload.target ?? previous?.target;
      const newKey = completePayload.expressionKey ?? previous?.expressionKey;
      const oldKey = previous?.expressionKey;

      if (target && (oldKey || newKey)) {
        dispatch(
          patchExpressionSchemaAction({
            target,
            removeKey: oldKey && oldKey !== newKey ? oldKey : null,
            addKey: newKey,
            valueType: schemaTypeOf(completePayload.valueType ?? previous?.valueType),
          }),
        );
      }

      dispatch(showMessageAction({ message: SUCCESS_MESSAGES.UPDATE, variant: 'success' }));

      return completePayload;
    } finally {
      dispatch(setLoadingAction(false));
    }
  },
  ERROR_MESSAGES.UPDATE,
);

export const deleteCustomFieldAction = catchAsyncActionError(
  payload => async (dispatch, getState) => {
    dispatch(setLoadingAction(true));
    dispatch(clearErrorAction());

    const { id } = payload;

    if (!id) {
      throw new Error('Missing ID for delete operation');
    }

    try {
      // Capture the pre-delete CF so we know which schema entry to remove.
      const previous = selectCustomFields(getState()).find(field => field.id === id) ?? null;

      await customFieldDefinitionsApi.deleteCustomFieldDefinition(id);

      dispatch({
        type: CUSTOM_FIELDS_ACTION_TYPE.DELETE_CUSTOM_FIELD,
        payload: id,
      });

      if (previous?.target && previous?.expressionKey) {
        dispatch(
          patchExpressionSchemaAction({
            target: previous.target,
            removeKey: previous.expressionKey,
          }),
        );
      }

      dispatch(showMessageAction({ message: SUCCESS_MESSAGES.DELETE, variant: 'success' }));

      return id;
    } finally {
      dispatch(setLoadingAction(false));
    }
  },
  ERROR_MESSAGES.DELETE,
);

export const upsertCustomFieldOverrideAction = catchAsyncActionError(
  ({ id, ...data }) =>
    async dispatch => {
      if (!id) {
        throw new Error('Missing ID for override upsert');
      }

      dispatch(setLoadingAction(true));
      dispatch(clearErrorAction());

      try {
        const response = await customFieldDefinitionsApi.upsertCustomFieldDefinitionOverride(
          id,
          data,
        );

        dispatch({
          type: CUSTOM_FIELDS_ACTION_TYPE.UPSERT_CUSTOM_FIELD_OVERRIDE,
          payload: { id, override: response.data ?? null },
        });
        dispatch(showMessageAction({ message: SUCCESS_MESSAGES.UPDATE, variant: 'success' }));

        return response.data;
      } finally {
        dispatch(setLoadingAction(false));
      }
    },
  ERROR_MESSAGES.UPDATE,
);

export const deleteCustomFieldOverrideAction = catchAsyncActionError(
  ({ id }) =>
    async dispatch => {
      if (!id) {
        throw new Error('Missing ID for override delete');
      }

      dispatch(setLoadingAction(true));
      dispatch(clearErrorAction());

      try {
        await customFieldDefinitionsApi.deleteCustomFieldDefinitionOverride(id);

        dispatch({
          type: CUSTOM_FIELDS_ACTION_TYPE.DELETE_CUSTOM_FIELD_OVERRIDE,
          payload: { id },
        });
        dispatch(showMessageAction({ message: SUCCESS_MESSAGES.UPDATE, variant: 'success' }));

        return id;
      } finally {
        dispatch(setLoadingAction(false));
      }
    },
  ERROR_MESSAGES.UPDATE,
);
